import React, { useContext, useCallback, useEffect, useState, useMemo, useRef } from "react";
import {useDispatch} from "react-redux";
import {Button, Form, Modal} from "react-bootstrap";
import axios from "axios";
import BigInteger from 'big-integer';
import {loadStripe} from '@stripe/stripe-js';
import {Elements} from "@stripe/react-stripe-js";
import { authContext } from './../../contexts/AuthProvider';
import { useSelector, history } from "../../stores";
import {numberWithDelimiter} from "../../utils";
import {totalPrice as calcTotalPrice, emptyCard} from "../../components/cart/CartController";
import {AddressComponent, newEmptyAddress} from "../../components/address/AddressComponent";
import {Payment} from "../../components/stripe/Payment";
import LinkButton from "../../components/LinkButton";
import {updateCheckoutItem, fetchDeliveryTimeZone, fetchShippingFee, fetchTaxRate} from "./CheckoutController";
import styles from "./CheckoutPage.module.scss"
import { paginate, finishMounted } from "./../../stores/transition";
import CheckoutForm, { ValueInterface } from "./../../components/checkout/CheckoutForm";
import MemoForm from "./../../components/checkout/MemoForm";
import DeliveryDateForm, { IDeliveryDateForm } from "./../../components/delivery-date/DeliveryDateForm";
import Bank from "./../../components/checkout/Bank";
import Cod from "./../../components/checkout/Cod";
import RegisteredCard from "./../../components/checkout/RegisteredCard";
import { PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';
import Bugsnag from '@bugsnag/js'

enum PaymentMethod {
  Credit = 'credit',
  RegisteredCredit = 'registered_credit',
  Bank = 'bank',
  Cod = 'cod',
}

const COD_PRICE_LIMIT = 300000;

export const CheckoutPage = () => {
    const dispatch = useDispatch();
    const auth = useContext(authContext);
    const items = useSelector(state => state.checkout.items);
    const shippingFee = useSelector(state => state.checkout.shippingFee);
    const taxRate = useSelector(state => state.checkout.taxRate);
    const timezones = useSelector(state => state.checkout.timezones);
    const paymentMethodFee = useSelector(state => state.checkout.paymentMethodFee);
    const [selectableCod, setSelectableCod] = useState<boolean>(true);
    const [address, setAddress] = useState(newEmptyAddress());
    const [memo, setMemo] = useState('');
    const [card, setCard] = useState();
    const [errors, setErrors] = useState([]);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [formValues, setFormValues] = useState<ValueInterface>({ name: auth.user?.user_name ?? '', email: auth.user?.email || '', phone: auth.user?.tel || '' });
    const [deliveryDateValues, setDeliveryDateValues] = useState<IDeliveryDateForm>({ desired_delivery_date: undefined, desired_delivery_timezone: '' });
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.Credit);
    const [isPaymentComplete, setIsPaymentComplete] = useState(false);

    const stripePromise = useMemo(() => loadStripe(window.gon?.stripe_key), []);
    const [stripe, setStripe] = useState(null);
    const idempotencyKeyRef = useRef<string | null>(null);

    const [isAddressValid, setIsAddressValid] = useState(false);
    const [isCheckoutFormValid, setIsCheckoutFormValid] = useState(false);
    const isValid = isAddressValid && isCheckoutFormValid;

    // Update idempotency key when any input changes
    const refreshIdempotencyKey = useCallback(() => {
        idempotencyKeyRef.current = crypto.randomUUID();
    }, [items, paymentMethod, address, formValues, deliveryDateValues, memo]);

    useEffect(refreshIdempotencyKey, [refreshIdempotencyKey]);

    useEffect(() => {
        stripePromise.then(setStripe);
    }, [stripePromise]);

    const total = calcTotalPrice(items);
    // 丸目誤差が発生するので整数に変換してからceilする
    const tax = total ? Math.ceil((total + shippingFee + paymentMethodFee) * taxRate) : 0;
    const subTotal = total ? Math.ceil(total + shippingFee + paymentMethodFee) : 0;
    const totalPrice = total ?
      BigInteger(total)
        .plus(BigInteger(shippingFee))
        .plus(BigInteger(paymentMethodFee))
        .plus(BigInteger(tax))
        : 0;

    if (isMounted) dispatch(finishMounted());
    useEffect(() => {
        (async () => {
            await Promise.all([
              dispatch(fetchShippingFee(items.map(item => item.id))),
              dispatch(fetchTaxRate()),
              dispatch(fetchDeliveryTimeZone()),
              initAddress(),
              initCard(),
            ]);
            setIsMounted(true);
        })();
    }, []);

    useEffect(() => {
        if (paymentMethod !== PaymentMethod.Cod) return;
        if (totalPrice < COD_PRICE_LIMIT) return;
        setPaymentMethod(PaymentMethod.Credit);
        setSelectableCod(false);
        alert(`合計金額は${totalPrice}円と、${COD_PRICE_LIMIT}円を超えるため代引きは選択できません。`);
    }, [paymentMethodFee]);

    // Add navigation blocking
    useEffect(() => {
        const message = 'お支払い手続きが完了していません。このページを離れてもよろしいですか？';

        // Handle browser navigation (back, forward, reload, close)
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (total > 0 && !isPaymentComplete) {
                e.preventDefault();
                e.returnValue = message;
                return e.returnValue;
            }
        };

        // Handle history navigation
        let unblock: (() => void) | null = null;
        if (total > 0 && !isPaymentComplete) {
            unblock = history.block((location, action) => {
                if (window.confirm(message)) {
                    unblock?.();
                    return;
                } else {
                    // Navigation was cancelled, reset isPaginating
                    dispatch(finishMounted());
                    return false;
                }
            });
        }

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            unblock?.();
        };
    }, [total, isPaymentComplete, dispatch]);

    const initAddress = async (): Promise<void> => {
      if (!auth.user) return;
      const res = await axios.get('/api/address');
      const attributes = res.data?.data?.attributes
      setAddress(old => ({
        ...old,
        country: {
          code: attributes?.country?.code || old.country.code,
          name: attributes?.country?.name || old.country.name,
        },
        zip: attributes?.zip || old.zip,
        prefecture: attributes?.prefecture || old.prefecture,
        city: attributes?.city || old.city,
        address: attributes?.address || old.address,
        building_name: attributes?.building_name || old.building_name,
      }));
    };

    const initCard = async (): Promise<void> => {
      if (!auth.user) return;
      const res = await axios.get('/api/cards');
      setCard(res.data?.card);
    };

    const onPaymentMethodReady = async (payment?: StripePaymentMethod) => {
        try {
            const params = {items, memo, payment, address, payment_method: paymentMethod, ...formValues, ...deliveryDateValues};
            const headers = { 'Idempotency-Key': idempotencyKeyRef.current };
            const res = await axios.post('/api/checkout', params, { headers });
            if (res.data.require_action) {
                const { error: errorAction, paymentIntent } = await stripe.handleCardAction(res.data.payment_intent_client_secret);
                if (errorAction) {
                    console.error(errorAction);
                    refreshIdempotencyKey();
                    handleErrors([{ messages: errorAction.message || '3D認証に失敗しました。もう一度お試しください。' }]);
                    return;
                }

                await axios.post('/api/checkout', {
                    ...params,
                    payment_intent_id: paymentIntent.id,
                    order_id: res.data.order_id,
                }, { headers })
            }

            // Unblock navigation before transitioning
            setIsPaymentComplete(true);
            dispatch(emptyCard());

            // Wait a bit to ensure state updates are processed
            setTimeout(() => {
                dispatch(paginate('/checkout/complete'));
            }, 0);
        } catch(err) {
            console.error(err);
            if (err.response?.status !== 422) {
              throw err;
            }
            handleErrors(err.response?.data?.errors);
        }
    };

    const handleErrors = errors => {
        errors?.map((error, i) => {
            if (error?.messages?.inventory && items?.[i]) {
                dispatch(updateCheckoutItem([items[i], error.messages.available]));
            }
        });
        setErrors(errors);
        setShowErrorDialog(true);
    };

    const printError = error => {
        if (error?.messages?.inventory) {
            return error.messages.inventory + '';
        } else if (error?.messages) {
            return error.messages + '';
        }

        return error;
    }

    const closeDialog = setShowErrorDialog.bind(null, false);

    const handleOnChangeform = (values: ValueInterface): void => setFormValues({...values});

    const handleOnSubmitBank = async (e: React.FormEvent): Promise<void> => {};

    const handleOnChangeMemo = (e: React.ChangeEvent<HTMLInputElement>): void => setMemo(e.target.value);

    const onChangePaymentMethod = (e: React.ChangeEvent<HTMLInputElement>): void =>
      setPaymentMethod(e.currentTarget.value as PaymentMethod);

    return <div className="container">
        <table className="table table-striped">
            <tbody>
                {
                    items.map((item, i) =>
                        <tr key={`checkout-detail-item-${i}`} className={item.amount ? '' : styles.unavailableItem}>
                            <td>{item.name}</td>
                            <td className={styles.price}>{numberWithDelimiter(item.price)}円</td>
                            <td className={styles.price}>{item.amount}本</td>
                            <td className={styles.price}>{numberWithDelimiter(item.price * item.amount)}円</td>
                        </tr>)
                }
                <tr>
                    <td/>
                    <td/>
                    <td>Shipping</td>
                    <td className={styles.price}>{numberWithDelimiter(totalPrice ? shippingFee : 0)}円</td>
                </tr>
                <tr>
                    <td/>
                    <td/>
                    <td>Payment method fee</td>
                    <td className={styles.price}>{numberWithDelimiter(totalPrice ? paymentMethodFee : 0)}円</td>
                </tr>

                <tr>
                    <td/>
                    <td/>
                    <td>Subtotal</td>
                    <td className={styles.price}>{numberWithDelimiter(subTotal)}円</td>
                </tr>
                <tr>
                    <td/>
                    <td/>
                    <td>Tax</td>
                    <td className={styles.price}>{numberWithDelimiter(tax)}円</td>
                </tr>
                <tr>
                    <td/>
                    <td/>
                    <td>Total</td>
                    <td className={styles.price}>{numberWithDelimiter(totalPrice)}円</td>
                </tr>
            </tbody>
        </table>
        {
            total > 0 &&
            <>
              <Form.Group>
                <Form.Label> 支払方法 </Form.Label>
                <Form.Check type="radio"
                  custom
                  label="クレジットカード"
                  id="paymentCredit"
                  name="paymentMethod"
                  value={PaymentMethod.Credit}
                  onChange={onChangePaymentMethod}
                  className={styles.check}
                  checked={paymentMethod === PaymentMethod.Credit} />
                <Form.Check type="radio"
                  custom
                  label="登録済みクレジットカード"
                  id="paymentRegisteredCredit"
                  name="paymentMethod"
                  value={PaymentMethod.RegisteredCredit}
                  onChange={onChangePaymentMethod}
                  className={styles.check}
                  disabled={!card}
                  checked={paymentMethod === PaymentMethod.RegisteredCredit} />
                <Form.Check type="radio"
                  custom
                  label="銀行振込"
                  id="paymentBank"
                  name="paymentMethod"
                  value={PaymentMethod.Bank}
                  onChange={onChangePaymentMethod}
                  className={styles.check}
                  checked={paymentMethod === PaymentMethod.Bank} />
                <Form.Check type="radio"
                  custom
                  label="代引き"
                  id="paymentCod"
                  name="paymentMethod"
                  value={PaymentMethod.Cod}
                  onChange={onChangePaymentMethod}
                  className={styles.check}
                  disabled={(totalPrice > COD_PRICE_LIMIT) || !selectableCod}
                  checked={paymentMethod === PaymentMethod.Cod} />
                <small className="text-muted">
                  ※総額が30万円以上の場合、代引きはご利用いただけません。<br/>
                  ※代引き手数料はお客様負担となります。(料金については<a className={styles.link} href="https://faq.kuronekoyamato.co.jp/app/answers/detail/a_id/1579/~/%E3%80%8C%E5%AE%85%E6%80%A5%E4%BE%BF%E3%82%B3%E3%83%AC%E3%82%AF%E3%83%88%E3%80%8D%E3%81%AE%E4%BB%A3%E9%87%91%E5%BC%95%E6%8F%9B%28%E4%BB%A3%E5%BC%95%E3%81%8D%29%E6%89%8B%E6%95%B0%E6%96%99%E3%82%92%E3%80%81%E6%95%99%E3%81%88%E3%81%A6%E3%81%8F%E3%81%A0%E3%81%95%E3%81%84%E3%80%82" target="_blank">こちら</a>)
                </small>
              </Form.Group>

              {paymentMethod === PaymentMethod.Credit &&
                  <Payment isValid={isValid} onPaymentMethodReady={onPaymentMethodReady}
                      stripePromise={stripePromise}
                      {...formValues}
                      onCardChange={refreshIdempotencyKey}
                      topChildren={<>
                        <CheckoutForm onChange={handleOnChangeform} onValidityChange={setIsCheckoutFormValid} {...formValues} />
                        <AddressComponent onAddressChange={setAddress} onValidityChange={setIsAddressValid} address={address} required />
                        <DeliveryDateForm timezones={timezones} onChange={setDeliveryDateValues} values={deliveryDateValues} />
                      </>}
                      bottomChildren={<MemoForm value={memo} onChange={handleOnChangeMemo} />} />
              }
              {paymentMethod === PaymentMethod.RegisteredCredit &&
                  <RegisteredCard isValid={isValid} onSubmit={() => onPaymentMethodReady()} card={card}
                      stripePromise={stripePromise}
                      topChildren={<>
                        <CheckoutForm onChange={handleOnChangeform} onValidityChange={setIsCheckoutFormValid} {...formValues} />
                        <AddressComponent onAddressChange={setAddress} onValidityChange={setIsAddressValid} address={address} required />
                        <DeliveryDateForm timezones={timezones} onChange={setDeliveryDateValues} values={deliveryDateValues} />
                      </>}
                      bottomChildren={<MemoForm value={memo} onChange={handleOnChangeMemo} />} />
              }
              {paymentMethod === PaymentMethod.Bank &&
                <Bank isValid={isValid} onSubmit={e => onPaymentMethodReady()}
                    topChildren={<>
                      <CheckoutForm onChange={handleOnChangeform} onValidityChange={setIsCheckoutFormValid} {...formValues} />
                      <AddressComponent onAddressChange={setAddress} onValidityChange={setIsAddressValid} address={address} required />
                      <DeliveryDateForm timezones={timezones} onChange={setDeliveryDateValues} values={deliveryDateValues} />
                    </>}
                    bottomChildren={<MemoForm value={memo} onChange={handleOnChangeMemo} />} />
              }
              {paymentMethod === PaymentMethod.Cod && (
                <Cod isValid={isValid} onSubmit={e => onPaymentMethodReady()} price={totalPrice}>
                    <CheckoutForm onChange={handleOnChangeform} onValidityChange={setIsCheckoutFormValid} {...formValues} />
                    <AddressComponent onAddressChange={setAddress} onValidityChange={setIsAddressValid} address={address} required />
                    <DeliveryDateForm timezones={timezones} onChange={setDeliveryDateValues} values={deliveryDateValues} />
                    <MemoForm value={memo} onChange={handleOnChangeMemo} />
                </Cod>
              )}
            </>
        }
        {
            total === 0 &&
            <>
                <p className="mb-3">カートは空です</p>
                <LinkButton to="/" className="w-100">戻る</LinkButton>
            </>
        }

        <Modal show={showErrorDialog} backdrop="static"
               onHide={closeDialog} centered>
            <Modal.Header closeButton>
                <Modal.Title>お支払いを完了できませんでした</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-3">
                  お客様のご注文はまだ確定しておりません。
                  <br />
                  以下の内容をご確認いただき、もう一度お手続きをお願いいたします。
                </p>
                <ul className={`${styles.errorList} mb-3`}>
                    {errors.map((error, i) => <li key={i} className={`${styles.errorListItem} text-danger`}>{printError(error)}</li>)}
                </ul>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDialog}>閉じる</Button>
                </Modal.Footer>
            </Modal.Body>
        </Modal>
    </div>;
};
